/**
 * Gift packaging add-on card in the cart.
 *
 * - Inside the Essential Cart drawer (#essential-cart-drawer-portal): the card is
 *   inserted right under the line items whenever the cart holds something else
 *   than the packaging itself.
 * - On the theme cart page / Dawn drawer: any element with [data-jq-addon-slot]
 *   receives the same card.
 * Ticking adds the packaging variant (quantity 1); the card then disappears and the
 * packaging shows as a normal line item. Removing that line brings the card back.
 * Configuration comes from snippets/jewelique-cart-addon.liquid.
 */
(() => {
  if (customElements.get('jewelique-cart-addon')) return;

  const PORTAL = '#essential-cart-drawer-portal';
  const LIST = '[data-essential-cart-element="cart-drawer-item-list"]';

  class JeweliqueCartAddon extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.root = (this.dataset.root || '/').replace(/\/?$/, '/');
      this.variant = Number(this.dataset.variant);
      this.template = this.querySelector('template[data-jq-addon-card]');
      if (!this.variant || !this.template) return;
      // Free from a cart amount: threshold in hundredths (cart.js units), pieces that never count.
      this.freeEnabled = this.dataset.freeEnabled === 'true';
      this.freeThreshold = parseInt(this.dataset.freeThreshold || '0', 10) || 0;
      this.exclude = (this.dataset.exclude || '').split(',').map((h) => h.trim().toLowerCase()).filter(Boolean);
      this.cart = null;
      this.busy = false;
      // Sold out since the page was rendered: check the product once now and again before
      // every add; when it is gone, every packaging card disappears.
      this.soldOut = false;
      this.checkStock();

      // Capture phase + stopPropagation: Dawn's <cart-items> treats any bubbling
      // change event as a quantity edit, which our checkbox is not.
      document.addEventListener(
        'change',
        (event) => {
          const input = event.target;
          if (!input?.classList?.contains('jq-addon__check')) return;
          event.stopPropagation();
          this.toggle(input);
        },
        true
      );
      document.addEventListener('jewelique:cart', (event) => {
        if (event.detail) this.adopt(event.detail, false);
      });
      this.interceptFetch();

      const discover = () => {
        const portal = document.querySelector(PORTAL);
        if (!portal) return false;
        this.portal = portal;
        new MutationObserver((records) => {
          if (records.some((r) => !r.target.closest?.('.jq-addon-slot'))) this.schedule();
        }).observe(portal, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'open', 'class'] });
        this.schedule();
        return true;
      };
      if (!discover()) {
        const observer = new MutationObserver(() => {
          if (discover()) observer.disconnect();
        });
        observer.observe(document.body, { childList: true, subtree: true });
        setTimeout(() => observer.disconnect(), 15000);
      }
      // Theme cart page: slots rendered by Liquid.
      this.schedule();
    }

    schedule() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.refresh(), 120);
    }

    async refresh() {
      if (this.cart && Date.now() - this.cartAt < 1500) return this.render();
      try {
        const response = await fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (response.ok) this.adopt(await response.json(), false);
      } catch (error) {
        /* leave the cart as is */
      }
    }

    adopt(cart, announce) {
      if (!cart || !Array.isArray(cart.items)) return;
      this.cart = cart;
      this.cartAt = Date.now();
      this.render();
      if (announce) document.dispatchEvent(new CustomEvent('jewelique:cart', { detail: cart }));
    }

    interceptFetch() {
      if (window.__jqAddonFetchPatched || typeof window.fetch !== 'function') return;
      window.__jqAddonFetchPatched = true;
      const original = window.fetch;
      const fullCart = /\/cart(?:\/(?:add|change|update|clear))?\.js(?:[?#]|$)/;
      window.fetch = (...args) => {
        const promise = original.apply(window, args);
        try {
          const input = args[0];
          const url = typeof input === 'string' ? input : input?.url || '';
          if (fullCart.test(url) && !/\/cart\/add\.js/.test(url)) {
            promise.then((response) => {
              if (!response.ok) return;
              response.clone().json().then((json) => this.adopt(json, false)).catch(() => {});
            }).catch(() => {});
          } else if (/\/cart\/add\.js/.test(url)) {
            promise.then(() => this.schedule()).catch(() => {});
          }
        } catch (error) {
          /* never interfere with the original call */
        }
        return promise;
      };
    }

    async checkStock() {
      const handle = this.dataset.handle;
      if (!handle) return;
      try {
        const response = await fetch(`${this.root}products/${handle}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!response.ok) return;
        const product = await response.json();
        const variant = (product.variants || []).find((v) => v.id === this.variant);
        const soldOut = !variant || variant.available === false;
        if (soldOut !== this.soldOut) {
          this.soldOut = soldOut;
          this.render();
        }
      } catch (error) {
        /* keep the last known state */
      }
    }

    line() {
      return this.cart?.items?.find((item) => item.variant_id === this.variant) || null;
    }

    /** Show the card only while the cart holds something else and the packaging is not in it yet
     *  (once added it appears as a normal line item, like the Essential add-on does). */
    wanted() {
      if (this.soldOut) return false;
      const items = this.cart?.items || [];
      return !this.line() && items.some((item) => item.variant_id !== this.variant);
    }

    card() {
      const node = this.template.content.firstElementChild.cloneNode(true);
      node.querySelector('.jq-addon__check').checked = Boolean(this.line());
      return node;
    }

    /** What the customer pays for everything except the packaging, the exchange fee and exchange lines.
     *  Taken from the cart total minus the excluded lines: the per-line prices are rounded (a set
     *  can read 9.99 next to 19.90 for a 29.90 tier), the total is exact. */
    payable() {
      const cart = this.cart || {};
      let excluded = 0;
      for (const item of cart.items || []) {
        const skip =
          item.variant_id === this.variant ||
          this.exclude.includes(String(item.handle || '').toLowerCase()) ||
          (item.properties && item.properties._exchange);
        if (skip) excluded += item.final_line_price || 0;
      }
      return Math.max((cart.total_price || 0) - excluded, 0);
    }

    money(cents) {
      const format = this.dataset.moneyFormat || '{{amount}}';
      const [whole, fraction] = (cents / 100).toFixed(2).split('.');
      const group = (s, sep) => s.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
      let text;
      if (/amount_no_decimals_with_comma_separator/.test(format)) text = group(whole, '.');
      else if (/amount_no_decimals_with_space_separator/.test(format)) text = group(whole, ' ');
      else if (/amount_no_decimals/.test(format)) text = whole;
      else if (/amount_with_comma_separator/.test(format)) text = group(whole, '.') + ',' + fraction;
      else if (/amount_with_space_separator/.test(format)) text = group(whole, ' ') + ',' + fraction;
      else if (/amount_with_apostrophe_separator/.test(format)) text = group(whole, "'") + '.' + fraction;
      else text = group(whole, ',') + '.' + fraction;
      return format.replace(/\{\{\s*amount[a-z_]*\s*\}\}/g, text).replace(/<[^>]+>/g, '').trim();
    }

    /** Free-from-amount state of every card: progress line, or the reached state. */
    decorate() {
      if (!this.freeEnabled || !this.freeThreshold) return;
      const payable = this.payable();
      const reached = payable >= this.freeThreshold;
      const remaining = this.money(Math.max(this.freeThreshold - payable, 0));
      document.querySelectorAll('.jq-addon').forEach((card) => {
        const line = card.querySelector('[data-jq-addon-free]');
        const price = card.querySelector('[data-jq-addon-price]');
        if (!line || !price) return;
        card.classList.toggle('jq-addon--free', reached);
        if (reached) {
          line.textContent = this.dataset.textFreeReached || '';
          if (!price.dataset.original) price.dataset.original = price.textContent;
          if (this.dataset.textFreeLabel) {
            price.textContent = this.dataset.textFreeLabel;
            price.classList.add('jq-addon__price--free');
          }
        } else {
          line.textContent = (this.dataset.textFreeProgress || '').replace('{remaining}', remaining);
          if (price.dataset.original) price.textContent = price.dataset.original;
          price.classList.remove('jq-addon__price--free');
        }
        line.hidden = !line.textContent;
      });
    }

    render() {
      if (!this.cart) return;
      const show = this.wanted();
      const checked = Boolean(this.line());

      // Essential Cart drawer
      if (this.portal) {
        const list = this.portal.querySelector(LIST);
        let slot = this.portal.querySelector('.jq-addon-slot');
        if (list && show) {
          if (!slot) {
            slot = document.createElement('div');
            slot.className = 'jq-addon-slot';
            slot.setAttribute('data-jq-addon-slot', '');
            slot.append(this.card());
            list.after(slot);
          } else if (slot.previousElementSibling !== list) {
            list.after(slot);
          }
        } else if (slot) {
          slot.remove();
        }
      }

      // Theme cart page / Dawn drawer slots
      document.querySelectorAll('[data-jq-addon-slot]').forEach((slot) => {
        if (this.portal && this.portal.contains(slot)) return;
        if (!show) {
          slot.innerHTML = '';
          return;
        }
        if (!slot.querySelector('.jq-addon')) slot.append(this.card());
      });

      document.querySelectorAll('.jq-addon__check').forEach((input) => {
        if (!this.busy) input.checked = checked;
      });
      this.decorate();
    }

    /** Theme cart page: re-render Dawn's cart sections the way cart.js does after a quantity change. */
    async refreshCartPage() {
      const sections = [
        { id: 'main-cart-items', selector: '.js-contents' },
        { id: 'main-cart-footer', selector: '.js-contents' },
        { id: 'cart-icon-bubble', selector: '.shopify-section' },
        { id: 'cart-live-region-text', selector: '.shopify-section' },
      ]
        .map((section) => ({ ...section, element: document.getElementById(section.id) }))
        .filter((section) => section.element)
        // JSON templates name the section (e.g. "cart-footer"); Dawn stores it in data-id.
        .map((section) => ({ ...section, key: section.element.dataset.id || section.id }));
      if (!sections.some((section) => section.id === 'main-cart-items')) return;
      try {
        const response = await fetch(`${this.root}cart?sections=${sections.map((s) => s.key).join(',')}`, { cache: 'no-store' });
        if (!response.ok) return;
        const html = await response.json();
        sections.forEach((section) => {
          // Same rule as Dawn's cart.js: replace the inner wrapper when there is one, else the element itself.
          const target = section.element.querySelector(section.selector) || section.element;
          const source = new DOMParser().parseFromString(html[section.key] || '', 'text/html').querySelector(section.selector);
          if (source) target.innerHTML = source.innerHTML;
        });
        const items = document.querySelector('cart-items');
        if (items) items.classList.toggle('is-empty', !this.cart?.item_count);
      } catch (error) {
        /* the cart itself is already updated; the page just keeps its old numbers */
      }
    }

    async toggle(input) {
      if (this.busy) {
        input.checked = !input.checked;
        return;
      }
      this.busy = true;
      const cards = [...document.querySelectorAll('.jq-addon')];
      cards.forEach((card) => card.classList.add('jq-addon--busy'));
      const add = input.checked;
      try {
        let response;
        if (add) {
          await this.checkStock();
          if (this.soldOut) throw new Error('packaging sold out');
          response = await fetch(`${this.root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: this.variant, quantity: 1 }] }),
          });
          if (!response.ok) throw new Error(`cart/add.js ${response.status}`);
          const cart = await fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
          this.adopt(await cart.json(), true);
        } else {
          const line = this.line();
          if (line) {
            response = await fetch(`${this.root}cart/change.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: line.key, quantity: 0 }),
            });
            if (!response.ok) throw new Error(`cart/change.js ${response.status}`);
            this.adopt(await response.json(), true);
          }
        }
        await this.refreshCartPage();
        // Essential Cart drawer: ask it to redraw from the cart it now has.
        try {
          window.essentialCartApi?.refreshCart?.();
        } catch (error) {
          /* the drawer also watches cart calls itself */
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'jewelique-cart-addon', cartData: this.cart });
        }
      } catch (error) {
        input.checked = !add;
        // A failed add (sold out meanwhile) removes the cards instead of leaving a dead checkbox.
        if (add && /422|sold out/.test(String(error && error.message))) {
          this.soldOut = true;
        }
        if (console && console.warn) console.warn('[jewelique-cart-addon]', error);
      } finally {
        this.busy = false;
        cards.forEach((card) => card.classList.remove('jq-addon--busy'));
        this.schedule();
      }
    }
  }

  customElements.define('jewelique-cart-addon', JeweliqueCartAddon);
})();
